<template>
  <div
    class="w-full min-h-128 md:py-32 py-12 relative"
    :style="`background: linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.2)),  url('${bgImage}') center/cover no-repeat`"
  >
    <div
      class="max-w-screen-xl md:space-y-16 space-y-6 px-2 xl:space-y-0 xl:space-x-16 xl:mx-auto flex flex-col items-stretch lg:flex-row lg:justify-between lg:items-center"
    >
      <div class="flex flex-col md:basis-1/2 md:flex-shrink-0">
        <h2 class="text-5xl text-white">Reviews</h2>
        <p class="text-xl md:text-2xl text-white">
          What our customers say about us
        </p>
      </div>

      <div class="md:w-1/2 w-full relative">
        <Carousel
          ref="carouselRef"
          class="testimonials-carousel"
          @mounted="onMounted"
          @slide-change="onSlideChange"
          @left-bound="onLeftBound"
          @right-bound="onRightBound"
        >
          <Slide v-for="(testimonial, index) in testimonials" :key="index">
            <div
              class="py-24 px-16 bg-white rounded-lg w-full border-t-4 border-b-4 testimonial"
            >
              <div v-if="testimonial.stars" class="flex items-center">
                <div
                  v-for="i in testimonial.stars"
                  :key="i"
                  class="text-yellow-500 text-2xl"
                >
                  &#9733;
                </div>
              </div>
              <p class="text-xl italic whitespace-pre-line" v-if="testimonial.quote">
                "{{ testimonial.quote }}"
              </p>
              <p>{{ testimonial.name }}</p>
              <p>
                <em>{{ testimonial.source }}</em>
              </p>
            </div>
          </Slide>
        </Carousel>

        <!-- Navigation Dots -->
        <div
          v-if="showNavigation && testimonials.length > 1"
          class="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[400] flex space-x-3"
        >
          <button
            v-for="(testimonial, index) in testimonials"
            :key="index"
            @click="goToSlide(index)"
            class="min-w-[24px] min-h-[24px] flex items-center justify-center bg-transparent focus:outline-none"
            :aria-label="`Go to testimonial ${index + 1}`"
          >
            <span
              class="block w-3 h-3 rounded-full transition-all duration-300 border-accent border-2"
              :class="
                selectedDot === index
                  ? 'scale-110 bg-none'
                  : 'bg-accent bg-opacity-20 hover:bg-opacity-75 cursor-pointer'
              "
            ></span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { Carousel, Slide } from "vue-snap";

interface Testimonial {
  quote: string;
  name: string;
  source: string;
  stars: number;
}

export default defineComponent({
  name: "TestimonialsVueSnap",
  components: {
    Carousel,
    Slide,
  },
  props: {
    testimonials: {
      type: Array as () => Testimonial[],
      required: true,
      default: () => [],
      validator: (value: any) => Boolean(value),
    },
    bgImage: {
      type: String,
      required: true,
      default: () => "/images/website/welder.jpg",
      validator: (value: any) => Boolean(value),
    },
    autoplayInterval: {
      type: Number,
      default: 6000,
    },
    enableAutoplay: {
      type: Boolean,
      default: true,
    },
    showNavigation: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentSlideIndex: 0,
      isAtLeftBound: false,
      isAtRightBound: false,
      autoplayTimer: null as NodeJS.Timeout | null,
      isHovering: false,
      isCarouselMounted: false,
      selectedDot: 0,
    };
  },
  methods: {
    onMounted() {
      this.isCarouselMounted = true;
      this.startAutoplay();
    },
    onSlideChange(newIndex: number) {
      this.currentSlideIndex = newIndex;

      // Reset autoplay timer when slide changes (either by autoplay or manual navigation)
      if (this.enableAutoplay && !this.isHovering) {
        this.restartAutoplay();
      }
    },
    onLeftBound(isAtBound: boolean) {
      this.isAtLeftBound = isAtBound;
    },
    onRightBound(isAtBound: boolean) {
      this.isAtRightBound = isAtBound;
    },
    startAutoplay() {
      if (
        !this.enableAutoplay ||
        this.testimonials.length <= 1 ||
        !this.isCarouselMounted ||
        this.isHovering
      )
        return;

      this.autoplayTimer = setInterval(() => {
        if (!this.isHovering && this.$refs.carouselRef) {
          try {
            const carousel = this.$refs.carouselRef as any;
            if (carousel && typeof carousel.goToSlide === "function") {
              const nextIndex =
                (this.currentSlideIndex + 1) % this.testimonials.length;
              this.selectedDot = nextIndex;
              carousel.goToSlide(nextIndex);
            } else {
              console.warn(
                "Carousel ref not available or changeSlide method not found"
              );
            }
          } catch (error) {
            console.error("Error calling changeSlide:", error);
          }
        }
      }, this.autoplayInterval);
    },
    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    },
    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    },
    onMouseEnter() {
      this.isHovering = true;
      this.stopAutoplay();
    },
    onMouseLeave() {
      this.isHovering = false;
      this.startAutoplay();
    },
    goToSlide(index: number) {
      if (this.$refs.carouselRef && this.isCarouselMounted) {
        try {
          const carousel = this.$refs.carouselRef as any;
          if (carousel && typeof carousel.goToSlide === "function") {
            this.selectedDot = index;
            carousel.goToSlide(index);
            // Reset autoplay timer when manually navigating
            if (this.enableAutoplay && !this.isHovering) {
              this.restartAutoplay();
            }
          } else {
            console.warn(
              "Carousel ref not available or goToSlide method not found"
            );
          }
        } catch (error) {
          console.error("Error calling goToSlide:", error);
        }
      }
    },
    changeSlide(direction: number) {
      if (this.$refs.carouselRef && this.isCarouselMounted) {
        try {
          const carousel = this.$refs.carouselRef as any;
          if (carousel && typeof carousel.changeSlide === "function") {
            carousel.changeSlide(direction);
            // Reset autoplay timer when manually navigating
            if (this.enableAutoplay && !this.isHovering) {
              this.restartAutoplay();
            }
          } else {
            console.warn(
              "Carousel ref not available or changeSlide method not found"
            );
          }
        } catch (error) {
          console.error("Error calling changeSlide:", error);
        }
      }
    },
    nextSlide() {
      this.changeSlide(1);
    },
    previousSlide() {
      this.changeSlide(-1);
    },
  },
  mounted() {
    // Add event listeners for hover
    const carouselElement = this.$el;
    carouselElement.addEventListener("mouseenter", this.onMouseEnter);
    carouselElement.addEventListener("mouseleave", this.onMouseLeave);
  },
  beforeUnmount() {
    this.stopAutoplay();

    // Remove event listeners
    if (this.$el) {
      const carouselElement = this.$el;
      carouselElement.removeEventListener("mouseenter", this.onMouseEnter);
      carouselElement.removeEventListener("mouseleave", this.onMouseLeave);
    }
  },
  watch: {
    enableAutoplay(newVal: boolean) {
      if (newVal && !this.isHovering) {
        this.startAutoplay();
      } else {
        this.stopAutoplay();
      }
    },
    autoplayInterval() {
      if (this.enableAutoplay && !this.isHovering) {
        this.restartAutoplay();
      }
    },
  },
});
</script>

<style scoped>
/* VueSnap Carousel specific styles for testimonials */
.testimonials-carousel {
  width: 100%;
}

:deep(.vs-carousel__wrapper) {
  height: auto;
}

:deep(.vs-carousel__slide) {
  flex: 0 0 100%;
  width: 100%;
}

/* Hide arrow navigation */
:deep(.vs-carousel__arrows) {
  display: none !important;
}

.testimonial {
  min-height: 200px;
}
</style>
